import { useCallback, useEffect, useRef } from 'react';

const HISTORY_MARKER = 'sindikatModuleDetail';

/**
 * Keeps in-module detail/create/edit views in sync with browser history (mouse back).
 *
 * When `isActive` is true, pushes a history entry. Browser back calls `onPop`.
 * Returned `closeView` should replace UI "Nazaj" handlers.
 */
export default function useBrowserBackStack(isActive, onPop) {
  const ownsEntryRef = useRef(false);
  const onPopRef = useRef(onPop);
  onPopRef.current = onPop;

  useEffect(() => {
    if (!isActive) {
      if (ownsEntryRef.current) {
        ownsEntryRef.current = false;
        window.history.back();
      }
      return undefined;
    }

    if (!ownsEntryRef.current) {
      window.history.pushState({ [HISTORY_MARKER]: true }, '');
      ownsEntryRef.current = true;
    }

    const handlePopState = () => {
      if (!ownsEntryRef.current) return;
      ownsEntryRef.current = false;
      onPopRef.current();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isActive]);

  return useCallback(() => {
    if (ownsEntryRef.current) {
      window.history.back();
      return;
    }
    onPopRef.current();
  }, []);
}
